import { useState, useCallback, useEffect, useMemo } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { DriverMasterService } from '@/services/driverMasterService'
import { EarningsService } from '@/services/earningsService'
import { CompensationService } from '@/services/compensationService'
import { PeriodService } from '@/services/periodService'
import { DriverMaster } from '@/types/driverMaster'
import { Earning } from '@/types/earnings'
import { Compensation } from '@/types/compensation'

export interface DriverAdjustmentSummary {
  driverId: string
  driverName: string
  totalNetEarnings: number
  earningsByPlatform: { platform: string; netEarnings: number; earnings: Earning[] }[]
  totalCompensations: number
  totalDeductions: number
  compensations: Compensation[]
}

export interface WizardAdjustmentsState {
  isLoading: boolean
  error: string | null

  drivers: DriverMaster[]
  earnings: Earning[]
  compensations: Compensation[]

  // Derived
  driverSummaries: DriverAdjustmentSummary[]

  // UI state
  expandedDriverIds: Set<string>
  excludedDriverIds: Set<string>

  // Dialog state
  showAddDialog: boolean
  showBulkUploadDialog: boolean
  selectedDriverId: string | null
  editingCompensation: Compensation | null
}

const initialState: WizardAdjustmentsState = {
  isLoading: true,
  error: null,
  drivers: [],
  earnings: [],
  compensations: [],
  driverSummaries: [],
  expandedDriverIds: new Set(),
  excludedDriverIds: new Set(),
  showAddDialog: false,
  showBulkUploadDialog: false,
  selectedDriverId: null,
  editingCompensation: null
}

export interface WizardAdjustmentsController {
  state: WizardAdjustmentsState
  periodId: string

  // Data actions
  reloadData: () => Promise<void>
  deleteCompensation: (compensationId: string) => Promise<void>
  deleteEarning: (earningId: string) => Promise<void>

  // Exclusion actions
  toggleDriverExclusion: (driverId: string) => void
  saveExclusions: () => Promise<void>

  // UI actions
  toggleDriverExpanded: (driverId: string) => void
  expandDriver: (driverId: string) => void
  collapseDriver: (driverId: string) => void

  // Dialog actions
  openAddDialog: (driverId?: string) => void
  closeAddDialog: () => void
  openEditDialog: (compensation: Compensation) => void
  closeEditDialog: () => void
  openBulkUploadDialog: () => void
  closeBulkUploadDialog: () => void
}

export function useWizardAdjustmentsController(periodId: string): WizardAdjustmentsController {
  const { currentUser } = useAuth()
  const [state, setState] = useState<WizardAdjustmentsState>(initialState)

  const updateState = useCallback((updates: Partial<WizardAdjustmentsState>) => {
    setState(prev => ({ ...prev, ...updates }))
  }, [])

  // Load all data in parallel
  const loadData = useCallback(async () => {
    if (!currentUser) return

    updateState({ isLoading: true, error: null })

    try {
      const [drivers, earnings, compensations, period] = await Promise.all([
        DriverMasterService.getDrivers(currentUser),
        EarningsService.getEarningsByPeriod(currentUser, periodId),
        CompensationService.getCompensationsByPeriod(currentUser, periodId),
        PeriodService.getPeriod(currentUser, periodId)
      ])

      const excludedFromPeriod = new Set(period?.excludedDriverIds || [])

      updateState({
        drivers,
        earnings,
        compensations,
        excludedDriverIds: excludedFromPeriod,
        isLoading: false
      })
    } catch (error) {
      console.error('[WizardAdjustments] Failed to load data:', error)
      updateState({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Kunde inte ladda data'
      })
    }
  }, [currentUser, periodId, updateState])

  // Compute driver summaries from drivers, earnings, and compensations
  const driverSummaries = useMemo((): DriverAdjustmentSummary[] => {
    const { drivers, earnings, compensations } = state

    // Get driver IDs that have earnings in this period
    const driverIdsWithEarnings = new Set(earnings.map(e => e.driverId))

    // Also include drivers that have compensations but no earnings
    const driverIdsWithCompensations = new Set(compensations.map(c => c.driverId))

    // Combine both sets
    const relevantDriverIds = new Set([...driverIdsWithEarnings, ...driverIdsWithCompensations])

    // Build summaries for drivers with data
    const summaries: DriverAdjustmentSummary[] = []

    for (const driverId of relevantDriverIds) {
      const driver = drivers.find(d => d.id === driverId)
      if (!driver) continue

      // Get earnings for this driver
      const driverEarnings = earnings.filter(e => e.driverId === driverId)

      // Group earnings by platform
      const earningsByPlatformMap = new Map<string, { netEarnings: number; earnings: Earning[] }>()
      let totalNetEarnings = 0

      for (const earning of driverEarnings) {
        const platform = earning.platform || 'unknown'
        const current = earningsByPlatformMap.get(platform) || { netEarnings: 0, earnings: [] }
        current.netEarnings += earning.netEarnings
        current.earnings.push(earning)
        earningsByPlatformMap.set(platform, current)
        totalNetEarnings += earning.netEarnings
      }

      // Get compensations for this driver
      const driverCompensations = compensations.filter(c => c.driverId === driverId)

      let totalCompensations = 0
      let totalDeductions = 0

      for (const comp of driverCompensations) {
        if (comp.type === 'compensation') {
          totalCompensations += comp.amount
        } else {
          totalDeductions += comp.amount
        }
      }

      summaries.push({
        driverId,
        driverName: driver.primaryName,
        totalNetEarnings,
        earningsByPlatform: Array.from(earningsByPlatformMap.entries()).map(([platform, data]) => ({
          platform,
          netEarnings: data.netEarnings,
          earnings: data.earnings
        })),
        totalCompensations,
        totalDeductions,
        compensations: driverCompensations
      })
    }

    // Sort by driver name
    return summaries.sort((a, b) => a.driverName.localeCompare(b.driverName, 'sv'))
  }, [state.drivers, state.earnings, state.compensations])

  // Update state with computed summaries
  useEffect(() => {
    setState(prev => ({ ...prev, driverSummaries }))
  }, [driverSummaries])

  // Load data on mount
  useEffect(() => {
    loadData()
  }, [loadData])

  // Delete compensation
  const deleteCompensation = useCallback(async (compensationId: string) => {
    if (!currentUser) return

    try {
      await CompensationService.deleteCompensation(currentUser, compensationId)
      // Reload compensations
      const compensations = await CompensationService.getCompensationsByPeriod(currentUser, periodId)
      updateState({ compensations })
    } catch (error) {
      console.error('[WizardAdjustments] Failed to delete compensation:', error)
      throw error
    }
  }, [currentUser, periodId, updateState])

  // Delete earning
  const deleteEarning = useCallback(async (earningId: string) => {
    if (!currentUser) return

    try {
      await EarningsService.deleteEarning(currentUser, earningId)
      // Reload earnings
      const earnings = await EarningsService.getEarningsByPeriod(currentUser, periodId)
      updateState({ earnings })
    } catch (error) {
      console.error('[WizardAdjustments] Failed to delete earning:', error)
      throw error
    }
  }, [currentUser, periodId, updateState])

  // Exclusion actions
  const toggleDriverExclusion = useCallback((driverId: string) => {
    setState(prev => {
      const newExcluded = new Set(prev.excludedDriverIds)
      if (newExcluded.has(driverId)) {
        newExcluded.delete(driverId)
      } else {
        newExcluded.add(driverId)
      }
      return { ...prev, excludedDriverIds: newExcluded }
    })
  }, [])

  const saveExclusions = useCallback(async () => {
    if (!currentUser) return

    try {
      await PeriodService.updateExcludedDrivers(
        currentUser,
        periodId,
        Array.from(state.excludedDriverIds)
      )
    } catch (error) {
      console.error('[WizardAdjustments] Failed to save exclusions:', error)
      throw error
    }
  }, [currentUser, periodId, state.excludedDriverIds])

  // UI actions
  const toggleDriverExpanded = useCallback((driverId: string) => {
    setState(prev => {
      const newExpanded = new Set(prev.expandedDriverIds)
      if (newExpanded.has(driverId)) {
        newExpanded.delete(driverId)
      } else {
        newExpanded.add(driverId)
      }
      return { ...prev, expandedDriverIds: newExpanded }
    })
  }, [])

  const expandDriver = useCallback((driverId: string) => {
    setState(prev => {
      const newExpanded = new Set(prev.expandedDriverIds)
      newExpanded.add(driverId)
      return { ...prev, expandedDriverIds: newExpanded }
    })
  }, [])

  const collapseDriver = useCallback((driverId: string) => {
    setState(prev => {
      const newExpanded = new Set(prev.expandedDriverIds)
      newExpanded.delete(driverId)
      return { ...prev, expandedDriverIds: newExpanded }
    })
  }, [])

  // Dialog actions
  const openAddDialog = useCallback((driverId?: string) => {
    updateState({
      showAddDialog: true,
      selectedDriverId: driverId || null,
      editingCompensation: null
    })
  }, [updateState])

  const closeAddDialog = useCallback(() => {
    updateState({
      showAddDialog: false,
      selectedDriverId: null,
      editingCompensation: null
    })
  }, [updateState])

  const openEditDialog = useCallback((compensation: Compensation) => {
    updateState({
      showAddDialog: true,
      selectedDriverId: compensation.driverId,
      editingCompensation: compensation
    })
  }, [updateState])

  const closeEditDialog = useCallback(() => {
    updateState({
      showAddDialog: false,
      selectedDriverId: null,
      editingCompensation: null
    })
  }, [updateState])

  const openBulkUploadDialog = useCallback(() => {
    updateState({ showBulkUploadDialog: true })
  }, [updateState])

  const closeBulkUploadDialog = useCallback(() => {
    updateState({ showBulkUploadDialog: false })
  }, [updateState])

  // Reload data after mutations
  const reloadData = useCallback(async () => {
    if (!currentUser) return

    try {
      const compensations = await CompensationService.getCompensationsByPeriod(currentUser, periodId)
      updateState({ compensations })
    } catch (error) {
      console.error('[WizardAdjustments] Failed to reload compensations:', error)
    }
  }, [currentUser, periodId, updateState])

  return useMemo(() => ({
    state: { ...state, driverSummaries },
    periodId,
    reloadData,
    deleteCompensation,
    deleteEarning,
    toggleDriverExclusion,
    saveExclusions,
    toggleDriverExpanded,
    expandDriver,
    collapseDriver,
    openAddDialog,
    closeAddDialog,
    openEditDialog,
    closeEditDialog,
    openBulkUploadDialog,
    closeBulkUploadDialog
  }), [
    state,
    driverSummaries,
    periodId,
    reloadData,
    deleteCompensation,
    deleteEarning,
    toggleDriverExclusion,
    saveExclusions,
    toggleDriverExpanded,
    expandDriver,
    collapseDriver,
    openAddDialog,
    closeAddDialog,
    openEditDialog,
    closeEditDialog,
    openBulkUploadDialog,
    closeBulkUploadDialog
  ])
}
